<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-3">
        Let’s start your police virtual work experience!
      </heading>
      <p class="mb-5">
        To become a police officer, candidates must undergo a rigorous selection process that begins
        with submitting the initial application. There then follows a series of checks and
        evaluations to assess the candidate’s suitability for the police force.
      </p>
      <p class="mb-5">
        Though the exact details of this selection process vary from police force to police force,
        some common elements of the selection process include:
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5">
          <ul class="default-list">
            <li>Police Entrance Evaluation (academic skills assessments)</li>
            <li>Psychological Evaluation</li>
            <li>Physical Performance Evaluation</li>
            <li>Panel Interview</li>
            <li>Medical Checks</li>
            <li>Background Checks</li>
          </ul>
          <p class="mb-5">
            The characteristics, skills, and abilities that are assessed in the selection process,
            and during training college for successful applicants, are those that will be essential
            for performing the duties of a police officer to a high standard.
          </p>

          <p class="mb-3">
            <u><strong>Types of Police</strong></u>
          </p>
          <p class="mb-5">In Australia, there are two main branches of the police force:</p>
          <ol class="numbered-list">
            <li>State & Territory Police</li>
            <li>Australian Federal Police</li>
          </ol>
        </div>
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5 text-center">
          <img
            src="@/assets/images/experiences/policing/police_sign.png"
            class="img-responsive"
            role="presentation"
            style="margin: 0 auto"
            alt=""
          />
        </div>
      </div>

      <p class="mb-5">
        <strong>State police</strong> are responsible for general law enforcement and they perform
        most of the general policing throughout each state, though some state-government departments
        may have investigative powers in specific areas.
      </p>
      <p class="mb-5">
        The state police uphold local laws, preserve the peace, and prevent crime. They also provide
        assistance in the case of emergencies, deal with missing person reports, investigate crimes,
        and enforce road and traffic rules.
      </p>
      <p class="mb-5">
        The <strong>Australian Federal Police</strong> is the national policing agency and it is
        responsible for upholding and enforcing Commonwealth law both at home and overseas. The AFP
        also protects important political figures, such as the Australian Prime Minister, and high
        profile people, such as visiting foreign dignitaries.
      </p>
      <p class="mb-5">
        The AFP also handles border and international operations and combat illicit drugs, people
        trafficking, and sex tourism. Additionally, they have responsibility for counter-terrorism
        and security in Australia’s major airports.
      </p>
      <p class="mb-5">
        In Australia, both state and federal police officers are routinely armed. As well as a
        handgun and ammunition, a police officer’s duty belt usually holds handcuffs, an expandable
        baton, pepper spray, a taser, a 2-way radio, gloves, and a torch.
      </p>

      <p class="mb-3">
        <u><strong>Education Level</strong></u>
      </p>
      <p class="mb-5">
        There are varying requirements for joining the different State and Territory Police forces,
        with the Australian Federal Police Force having their specific requirements too.
      </p>
      <p class="mb-5">
        However, most forces require applicants to have completed their secondary education.
        Applicants who have completed courses in related areas of study such as law, criminal
        justice or administration may be looked upon more favourably.
      </p>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-3">
        How to Become an Australian Police Officer
      </heading>
      <p class="mb-5">
        There are several steps you'll need to follow if you want to become a police officer. This
        is known as the selection process. The main parts of the selection process you’ll need to
        know about are: (Click the down arrows on the right to get descriptions and examples of each
        one)
      </p>
      <accordion-panel aria-title="The Application Form">
        <template v-slot:title>
          <div>The Application Form</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            The first step on the road to becoming a police officer is submitting an application
            form.
          </p>
          <p class="mb-5">
            Before completing the application form, however, you’ll need to ensure you are eligible
            to apply. Check the website of the force you’re applying to for the minimum requirements
            to see that you meet their criteria before completing an application form.
          </p>
          <p class="mb-5">Some desirable qualities commonly looked for in applicants include:</p>
          <ul class="default-list" role="list">
            <li role="listitem">Honesty and integrity</li>
            <li role="listitem">Relevant educational and work experiences</li>
            <li role="listitem">Good physical and mental health</li>
          </ul>
          <p class="mb-5">
            Background checks will be performed, and various supporting documentation will be
            required to support your application.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="The Tests">
        <template v-slot:title>
          <div>The Tests</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            Suppose you are successful in the application stage. In that case, you’ll move to the
            next stage of the process where you’ll be asked to complete several tests to assess your
            mental, physical, and emotional suitability to be a member of the police force.
          </p>
          <p class="mb-5">
            You will most likely be competing against lots of candidates, so you’ll need to do
            everything possible to prepare yourself for the best chance of success. As mentioned,
            tests used vary from force to force, so be sure to spend time doing thorough research
            about the tests for the force you’re applying to. Some common types of test include:
          </p>
          <ul class="default-list" role="list">
            <li role="listitem">Maths tests</li>
            <li role="listitem">Written tests</li>
            <li role="listitem">Reasoning tests</li>
            <li role="listitem">Physical tests</li>
            <li role="listitem">Psychological tests</li>
          </ul>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="The Interview">
        <template v-slot:title>
          <div>The Interview</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            Usually, successful applicants will have been interviewed several times by the time they
            become a police officer.
          </p>
          <p class="mb-5">
            To maximise your chances of success, you’ll need to prepare well for each interview.
            Some things to consider include:
          </p>
          <ul class="default-list" role="list">
            <li role="listitem">Maintain high standards of presentation</li>
            <li role="listitem">Research the position thoroughly beforehand</li>
            <li role="listitem">Prepare your answers for likely questions</li>
            <li role="listitem">Practice making eye contact when speaking</li>
            <li role="listitem">Have a firm handshake</li>
            <li role="listitem">Be on time</li>
          </ul>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="The Police Academy">
        <template v-slot:title>
          <div>The Police Academy</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            If you pass through the previous stages successfully, you will become a recruit and your
            next stop will be Police Academy!
          </p>
          <p class="mb-5">
            At the police academy, you will complete a police officer training course. In lectures,
            seminars, and training sessions, you will learn about the many different aspects of
            police work.
          </p>
          <p class="mb-5">
            You’ll also be assessed in written tests, report writing exercises, practical
            examinations, and other types of assessments.
          </p>
          <p class="mb-5">
            If you complete the training course successfully, you will become a police constable and
            begin work.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="The Probationary Period">
        <template v-slot:title>
          <div>The Probationary Period</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            Once you have passed successfully through each element of the selection process, you’ll
            then continue your learning while serving your probationary period. This period can last
            several months but may vary from force to force.
          </p>
          <p class="mb-5">
            Your probationary period is designed to give you time to develop your capacity and build
            your competency. Your duties and responsibilities will gradually increase during this
            period and, at the end of it, you’ll be assessed and considered for a permanent
            position.
          </p>
        </template>
      </accordion-panel>
      <heading size="h2" style-size="h4" class="mb-3">Recruitment Process Video</heading>
      <p class="mb-5">
        A useful video from Victoria Police about the recruitment process with some useful hints on
        how to get some useful work experience before applying.
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch Recruitment process video"
            elem-id="video-btn-1"
            :video-image="videoTrigger1"
            :video-url="videos[1]"
          />
        </div>
      </div>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-3">Quick Details</heading>
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <p class="mb-5"><strong>Size:</strong> This is a very large occupation.</p>
          <p class="mb-5">
            <strong>Earnings:</strong> Full-time workers on an adult wage earn around $2,036 per
            week (higher than the average of $1,460). Earnings tend to be lower when starting and
            higher as experience grows.
          </p>
          <p class="mb-5">
            <strong>Full-time:</strong> Most work full-time (93%, much higher than the average of
            66%).
          </p>
          <p class="mb-5">
            <strong>Hours:</strong> Full-time workers spend around 43 hours per week at work
            (compared to an average of 44 hours).
          </p>
          <p class="mb-5">
            <strong>Age:</strong> The average age is 40 years (compared to the average of 40 years).
          </p>
          <p class="mb-5">
            <strong>Gender:</strong> 27% of workers are female (compared to the average of 48%).
          </p>
          <p class="mb-3">
            <u><strong>Tasks</strong></u>
          </p>
          <ul class="default-list">
            <li>
              Investigating and prosecuting offences committed in areas such as organised,
              corporate, and computer crime, environmental offences, drug trafficking, fraud,
              counterfeiting, and terrorism
            </li>
            <li>
              Securing and examining scenes of crimes and accidents to locate and obtain evidence
              for analysis
            </li>
            <li>Protecting witnesses and investigating official corruption</li>
            <li>Maintaining public order and safety</li>
            <li>
              Patrolling assigned areas to minimise the potential for public disturbance and crime
            </li>
            <li>
              Investigating accidents, crimes, minor offences, and citizens' complaints, gathering
              evidence, and pursuing, arresting, and interviewing suspected offenders
            </li>
            <li>
              Testing persons suspected of driving under the influence of alcohol and drugs and
              issuing infringement notices for traffic offences
            </li>
            <li>Directing and rerouting traffic at congested areas</li>
            <li>
              Attending community meetings and answering inquiries from the public where necessary
            </li>
            <li>Providing advice and assistance to victims of crime and their families</li>
            <li>Maintaining records and preparing reports</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/policing/jo1.png"
            class="mx-auto img-responsive"
            alt="Police job outlook: $2,036 weekly pay, strong future growth. Police Officers: 48,200 workers, high skill level, 93% full-time, 43 hours average full-time week, average age 40, 27% female."
          />
          <p class="mb-10 text-center">
            Sourced from <a href="https://www.joboutlook.gov.au/" target="_blank">Job Outlook</a>
          </p>
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-3">
        Highlights of Becoming a Police Officer
      </heading>
      <accordion-panel aria-title="An Opportunity to Do Good">
        <template v-slot:title>
          <div>An Opportunity to Do Good</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            As a police officer, you’ll frequently find yourself in a position where you can make a
            difference for the good in people’s lives.
          </p>
          <p class="mb-5">
            From helping at the scene of a road traffic accident to protecting a victim from a
            perpetrator of domestic violence, police officers encounter opportunities daily to serve
            their communities and improve the lives of those around them.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="Variety in Your Work">
        <template v-slot:title>
          <div>Variety in Your Work</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            If you hate monotony in your work, this might just be the job for you. As a police
            officer, no two days will be the same.
          </p>
          <p class="mb-5">
            Throughout your career, you’ll get the opportunity to sample many different areas of
            police work. You may well get a chance to investigate crime, give evidence in court,
            provide police protection, issue speeding tickets, perform first aid, and write reports.
            You may be offered further opportunities to specialise that appeal to you, such as
            becoming a K-9 officer, for example.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel aria-title="The Camaraderie">
        <template v-slot:title>
          <div>The Camaraderie</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            Not only do police officers protect their communities but, due to the risks of the job,
            they protect each other in a way that doesn’t happen in many other professions.
          </p>
          <p class="mb-5">
            While working as a police officer there will be times when only your colleagues will
            understand. You’ll rely on them for moral support in difficult times, just as much as
            you will rely on them for backup while out on the street. For many police officers,
            their colleagues become part of their extended family.
          </p>
        </template>
      </accordion-panel>

      <heading size="h2" style-size="h4" class="mb-3"> Did You Know This?</heading>
      <p class="mb-5">
        Australia's first police force was made up of the most well-behaved convicts!
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <img
            src="@/assets/images/experiences/policing/police_intro_1.png"
            alt=""
            class="mx-auto img-responsive"
            role="presentation"
          />
        </div>
      </div>
      <p class="mb-5">
        And… although policing of any kind is serious business, police too have their moments!
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch police having their moments video"
            elem-id="video-btn-2"
            :video-image="videoTrigger2"
            :video-url="videos[2]"
          />
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import CustomVideo from '@/components/CustomVideo.vue';

export default {
  components: { AccordionPanel, CustomVideo },
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/policing/police_officer_recruitment.jpg'),
      videoTrigger2: require('@/assets/images/experiences/policing/police_funny_moments.jpg'),
      videos: {
        1: 'https://www.youtube.com/embed/OlrD6uJXwGY',
        2:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/policing/police_funny_moments.mp4',
      },
    };
  },
};
</script>
